import { getGlobal } from "../global";

/**
 * Logging levels. Higher number means more verbose logs,
 * for example, with level 3, `info`, `warn`, or `error`
 * logs will show up in the console but `debug` and `log` won't.
 *
 * Semantics of specific levels:
 *  - debug: low-level debugging logs
 *  - log: common, higher-level debugging logs
 *  - info: helpful runtime information (even for stag/prod environments)
 *  - warn: potentially problematic situations; handled exceptions
 *  - error: definitely problematic situations; unhandled exceptions
 * @readonly
 * @enum {number}
 */
export const LogLevels = {
  DEBUG: 5,
  LOG: 4,
  INFO: 3,
  WARNING: 2,
  ERROR: 1,
  NONE: 0
};

/**
 * Public interface that any logger object must satisfy to be used with {@link setLogger}.
 * {@link NoOpLogger} is the built-in no-op stand-in; `DefaultLogger` is the production
 * implementation.
 *
 * @typedef {Object} LoggerInterface
 * @property {number} level
 *   Current log level; one of the {@link LogLevels} constants. Only messages at or below
 *   this level are emitted.
 * @property {function(number): void} setLevel
 *   Changes the active log level. Accepts a {@link LogLevels} constant.
 * @property {function(Object=): void} initialize
 *   One-time setup called during viewer initialisation. Receives the viewer options object.
 * @property {function(): void} shutdown
 *   Tears down any timers or resources created in `initialize`.
 * @property {function(Object): void} track
 *   Records a structured analytics or tracking event. The entry object should at minimum
 *   have a `category` string property.
 * @property {function(Object): void} updateRuntimeStats
 *   Accumulates a named runtime-stats entry. The entry must have a `name` property and an
 *   `aggregate` strategy (`"count"` or `"last"`).
 * @property {function(): void} reportRuntimeStats
 *   Flushes all accumulated runtime stats by forwarding them through {@link LoggerInterface#track}.
 * @property {Object<string, *>} runtimeStats
 *   Live map of accumulated runtime-stats values, keyed by stat name.
 * @property {Object<string, *>} environmentInfo
 *   Static environment metadata (touch, env, referer, version) captured during `initialize`.
 * @property {function(...*): void} debug
 *   Low-level debugging output. Emitted only when level >= {@link LogLevels.DEBUG}.
 * @property {function(...*): void} log
 *   General debugging output. Emitted only when level >= {@link LogLevels.LOG}.
 * @property {function(...*): void} info
 *   Informational output suitable for staging/production. Emitted when level >= {@link LogLevels.INFO}.
 * @property {function(...*): void} warn
 *   Warning output for handled or potentially problematic situations. Emitted when level >= {@link LogLevels.WARNING}.
 * @property {function(...*): void} error
 *   Error output for unhandled or definitely problematic situations. Emitted when level >= {@link LogLevels.ERROR}.
 */

/**
 * Logger class.
 * Depending on options.logLevel setting in initilize function, corresponding functions would be activated.
 * @implements {LoggerInterface}
 */
class NoOpLogger {

  level = LogLevels.NONE;

  /** @type {Object<string, *>} */
  runtimeStats = {};
  /** @type {Object<string, *>} */
  environmentInfo = {};

  /** @param {Object} [options] */
  initialize(options) {
    console.warn(`Logger initialized before one was provided, this is most likely a misconfiguration issue!`);
  }
  shutdown() {}
  /** @param {Object} _entry */
  track(_entry) {}
  /** @param {Object} _entry */
  updateRuntimeStats(_entry) {}
  reportRuntimeStats() {}
  /** @param {number} level */
  setLevel(level) {
    this.level = isNaN(Number(level)) ? this.level : level;
  }
  /** @param {...*} _args */
  debug(..._args) {}
  /** @param {...*} _args */
  log(..._args) {}
  /** @param {...*} _args */
  info(..._args) {}
  /** @param {...*} _args */
  warn(..._args) {}
  /** @param {...*} _args */
  error(..._args) {}
}


export let logger = new NoOpLogger();

/** @param {LoggerInterface} l */
export function setLogger(l) {
  if (!l) {
    // Prevent null/undefined errors from global scope access
    l = new NoOpLogger();
  }
  // Preserve the log level from the current logger if it exists
  if (logger.level !== undefined) {
    l.setLevel(logger.level);
  }

  logger = l;
  const avp = getGlobal()?.Autodesk?.Viewing?.Private;
  if (avp) {
    avp.logger = logger;
  }
}